<script setup lang="ts">
  /**
   * Buttons — 03-components/06-buttons · OSCloud DS 1.1.47
   */
  import { computed } from 'vue'

  type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger'
  type Size = 'default' | 'small' | 'large'

  const props = withDefaults(
    defineProps<{
      variant?: Variant
      size?: Size
      type?: 'button' | 'submit' | 'reset'
      disabled?: boolean
      loading?: boolean
      fullWidth?: boolean
      sweep?: boolean
      /**
       * Toggle state for a button that holds a value — a preset amount, a duration, a quantity.
       * Rendered as aria-pressed, never a modifier, and only the outline variant carries a
       * pressed appearance: an outline reads as "one of several", the filled variants read as
       * "the action". Leave undefined on a button that is not a toggle.
       */
      pressed?: boolean
      /**
       * Blocked (DS 06, aria-disabled): the disabled appearance, and just as inert — a click never
       * emits and never submits a form. Unlike `disabled`, the button stays focusable, so it is
       * still announced and reachable by keyboard.
       */
      blocked?: boolean
    }>(),
    {
      variant: 'primary',
      size: 'default',
      type: 'button',
      disabled: false,
      loading: false,
      fullWidth: false,
      sweep: false,
      pressed: undefined,
      blocked: false,
    },
  )

  const emit = defineEmits<{ click: [event: MouseEvent] }>()

  // A disabled-looking button is never clickable. Cancelling the click also stops a submit button
  // from submitting its form, including the implicit submit fired by Enter in a text field.
  const onClick = (event: MouseEvent) => {
    if (props.blocked) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }
    emit('click', event)
  }

  /**
   * The `variant` prop keeps its call-site name; the rendered modifier follows the design system,
   * which names this variant for what it does — it also cancels and revokes, not only deletes.
   */
  const MODIFIER_BY_VARIANT: Record<Variant, string> = {
    primary: 'primary',
    secondary: 'secondary',
    outline: 'outline',
    ghost: 'ghost',
    danger: 'destructive',
  }

  const variantModifier = computed(() => MODIFIER_BY_VARIANT[props.variant])
</script>

<template>
  <button
    class="button"
    :class="[
      `button--${variantModifier}`,
      {
        'button--small': size === 'small',
        'button--large': size === 'large',
        'button--full-width': fullWidth,
        'button--sweep': sweep,
      },
    ]"
    :type="type"
    :disabled="disabled || loading"
    :aria-busy="loading"
    :aria-pressed="pressed"
    :aria-disabled="blocked ? 'true' : undefined"
    @click="onClick"
  >
    <span v-if="loading" class="button__spinner" aria-hidden="true" />
    <span class="button__label"><slot /></span>
    <span v-if="$slots.icon && !loading" class="button__icon"><slot name="icon" /></span>
  </button>
</template>

<style scoped lang="scss">
  .button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    white-space: nowrap;
    height: 40px;
    padding: 0 20px;
    border: 0;
    border-radius: calc(var(--dk-radius) - 2px);
    font-family: var(--bf-font-display);
    font-size: 14px;
    font-weight: var(--bf-fw-semibold);
    letter-spacing: 0;
    text-decoration: none; /* so an anchor wearing .button never underlines */
    cursor: pointer;
    transition: all var(--dk-transition);

    &:focus-visible {
      outline: none;
      box-shadow:
        0 0 0 2px hsl(var(--dk-background)),
        0 0 0 4px hsl(var(--dk-ring));
    }

    &:disabled {
      cursor: not-allowed;
      transform: none;
    }

    &:active:not(:disabled) {
      transform: scale(0.98);
    }

    &__label {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &--primary {
      background: linear-gradient(to bottom, var(--dk-orange-hover), hsl(var(--dk-primary)));
      color: hsl(var(--dk-primary-foreground));
      box-shadow: 0 1px 2px rgba(234, 96, 10, 0.25);

      &:hover:not(:disabled) {
        box-shadow: 0 2px 10px rgba(234, 96, 10, 0.35);
        filter: brightness(1.05);
      }

      &:disabled {
        background: hsl(var(--dk-secondary));
        color: hsl(var(--dk-muted-foreground));
        box-shadow: none;
        filter: none;
      }
    }

    &--secondary {
      background: hsl(var(--dk-secondary));
      color: hsl(var(--dk-secondary-foreground));
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

      &:hover:not(:disabled) {
        background: hsl(var(--dk-secondary) / 0.8);
      }

      &:disabled {
        opacity: 0.5;
      }
    }

    &--outline {
      background: hsl(var(--dk-background));
      color: hsl(var(--dk-secondary-foreground));
      border: 1px solid hsl(var(--dk-input));
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

      &:hover:not(:disabled) {
        background: hsl(var(--dk-accent));
        color: hsl(var(--dk-accent-foreground));
      }

      &:disabled {
        opacity: 0.5;
      }

      /* Pressed is the Selection card's selected recipe, so a pressed button and
         a selected card say "chosen" the same way. The hairline is doubled by an
         inset shadow so the edge is 2px without moving the box. Hover does not
         override it. */
      &[aria-pressed='true'],
      &[aria-pressed='true']:hover:not(:disabled) {
        background: var(--bf-warning-soft);
        color: var(--bf-orange-dark);
        border-color: var(--bf-orange-dark);
        box-shadow: inset 0 0 0 1px var(--bf-orange-dark);
      }
    }

    &--ghost {
      background: transparent;
      color: hsl(var(--dk-secondary-foreground));

      &:hover:not(:disabled) {
        background: hsl(var(--dk-accent));
        color: hsl(var(--dk-accent-foreground));
      }

      &:disabled {
        opacity: 0.5;
      }
    }

    &--destructive {
      background: hsl(var(--dk-danger));
      color: hsl(var(--dk-danger-foreground));
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

      &:hover:not(:disabled) {
        background: hsl(var(--dk-danger) / 0.9);
      }

      &:disabled {
        opacity: 0.5;
      }
    }

    /* ─── Blocked action (aria-disabled, DS 1.1.55) ────────────────────
       `disabled` is for an action unavailable for a reason already on screen. An action blocked
       by a rule the user has not been told stays focusable and clickable so its click can name
       the rule (a toast, an inline notice): the consumer passes aria-disabled="true", which falls
       through to the <button>. Runtime state, never a modifier. Every value is copied from the
       variant's own :disabled recipe above and must stay identical to it; each :hover is
       neutralised because the hover rules are written :hover:not(:disabled). */
    &[aria-disabled='true'] {
      cursor: not-allowed;
    }

    &[aria-disabled='true']:active,
    &--sweep[aria-disabled='true']:hover {
      transform: none;
    }

    &--primary[aria-disabled='true'],
    &--primary[aria-disabled='true']:hover {
      background: hsl(var(--dk-secondary));
      color: hsl(var(--dk-muted-foreground));
      box-shadow: none;
      filter: none;
    }

    &--secondary[aria-disabled='true'],
    &--secondary[aria-disabled='true']:hover {
      background: hsl(var(--dk-secondary));
      opacity: 0.5;
    }

    &--outline[aria-disabled='true'],
    &--outline[aria-disabled='true']:hover {
      background: hsl(var(--dk-background));
      color: hsl(var(--dk-secondary-foreground));
      opacity: 0.5;
    }

    &--ghost[aria-disabled='true'],
    &--ghost[aria-disabled='true']:hover {
      background: transparent;
      color: hsl(var(--dk-secondary-foreground));
      opacity: 0.5;
    }

    &--destructive[aria-disabled='true'],
    &--destructive[aria-disabled='true']:hover {
      background: hsl(var(--dk-danger));
      opacity: 0.5;
    }

    &--small {
      height: 32px;
      padding: 0 12px;
      font-size: 12px;
    }

    &--large {
      height: 44px;
      padding: 0 32px;
    }

    &--full-width {
      width: 100%;
    }

    &--sweep {
      &:hover:not(:disabled) {
        transform: translateY(-1px);
      }

      &:active:not(:disabled) {
        transform: translateY(0) scale(0.99);
      }
    }

    &__icon,
    &__spinner {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    &__icon :deep(svg) {
      width: 16px;
      height: 16px;
    }

    &__spinner {
      width: 16px;
      height: 16px;
      border: 2px solid currentColor;
      border-top-color: transparent;
      border-radius: 50%;
      animation: button-spin 0.8s linear infinite;
    }
  }

  @keyframes button-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .button__spinner {
      animation-duration: 1.6s;
    }
  }
</style>
